import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { complete, lint, tokenize } from '../../sql-lint'
import type { CompletionContext, Diagnostic, Token, Vocabulary } from '../../sql-lint'
import { completeDql, lintDql, tokenizeDql } from '../../dql-lint'

/** One language module per dialect; the editor is otherwise dialect-blind. */
const LANG = {
  sql: { tokenize, lint, complete },
  dql: { tokenize: tokenizeDql, lint: lintDql, complete: completeDql },
} as const
export type EditorDialect = keyof typeof LANG

/**
 * A textarea with a highlight layer under it and a suggestion list beside it.
 *
 * The textarea keeps every native behaviour (selection, undo, IME, resize);
 * its text is painted transparent and a <pre> with the same metrics sits
 * behind it carrying the coloured tokens and the diagnostic underlines. The
 * two stay aligned because they share one font, one padding and one wrap
 * mode, and the pre follows the textarea's scroll.
 *
 * Both dialects are highlighted, linted and completed; `dialect` picks the
 * language module (sql-lint / dql-lint), everything else is shared.
 */
export function SqlEditor({
  value,
  onChange,
  onRun,
  dialect,
  vocabulary,
  placeholder,
  ariaLabel,
  lint = true,
}: {
  value: string
  onChange: (v: string) => void
  onRun: () => void
  dialect: EditorDialect
  vocabulary?: Vocabulary | null
  placeholder?: string
  ariaLabel: string
  /** false: highlight and complete, but show no problems (no list, no squiggles). */
  lint?: boolean
}) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const preRef = useRef<HTMLPreElement>(null)
  const [caret, setCaret] = useState(0)
  const [menu, setMenu] = useState<CompletionContext | null>(null)
  const [active, setActive] = useState(0)
  /** Set when the user dismissed the menu; cleared by the next edit. */
  const dismissed = useRef(false)

  const lang = LANG[dialect]
  const diagnostics = useMemo<Diagnostic[]>(() => (lint ? lang.lint(value, vocabulary) : []), [lint, lang, value, vocabulary])

  // Suggestions follow the caret, but only after an EDIT — moving the caret
  // with the arrows must not pop a menu on every word.
  const refreshMenu = (src: string, at: number) => {
    if (dismissed.current) {
      setMenu(null)
      return
    }
    const ctx = lang.complete(src, at, vocabulary)
    setMenu(ctx)
    setActive(0)
  }

  const edit = (next: string, nextCaret: number) => {
    dismissed.current = false
    onChange(next)
    setCaret(nextCaret)
    refreshMenu(next, nextCaret)
  }

  const accept = (i: number) => {
    if (!menu) return
    const item = menu.items[i]
    if (!item) return
    const next = value.slice(0, menu.from) + item.insert + value.slice(menu.to)
    const at = menu.from + item.insert.length
    onChange(next)
    setCaret(at)
    setMenu(null)
    // Restore focus + caret after React re-renders the value.
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (!ta) return
      ta.focus()
      ta.setSelectionRange(at, at)
    })
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      setMenu(null)
      onRun()
      return
    }
    if (!menu) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (a + 1) % menu.items.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (a + menu.items.length - 1) % menu.items.length)
    } else if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault()
      accept(active)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      dismissed.current = true
      setMenu(null)
    }
  }

  // Keep the highlight layer scrolled with the textarea.
  const syncScroll = () => {
    const ta = taRef.current
    const pre = preRef.current
    if (ta && pre) {
      pre.scrollTop = ta.scrollTop
      pre.scrollLeft = ta.scrollLeft
    }
  }
  useEffect(syncScroll, [value])

  const jumpTo = (d: Diagnostic) => {
    const ta = taRef.current
    if (!ta) return
    ta.focus()
    ta.setSelectionRange(d.start, d.end)
    setCaret(d.end)
  }

  return (
    <div className="sqe">
      <div className="sqe-stack">
        <pre ref={preRef} className="sqe-hl" aria-hidden="true">
          {highlight(lang.tokenize(value), diagnostics)}
          {'\n'}
        </pre>
        <textarea
          ref={taRef}
          className="q-input sqe-ta"
          data-sql=""
          data-dialect={dialect}
          value={value}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          rows={2}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-controls={menu ? 'sqe-menu' : undefined}
          placeholder={placeholder}
          onChange={(e) => edit(e.target.value, e.target.selectionStart)}
          onKeyDown={onKeyDown}
          onScroll={syncScroll}
          onClick={(e) => setCaret(e.currentTarget.selectionStart)}
          onBlur={() => setMenu(null)}
        />
        {menu ? (
          <div id="sqe-menu" className="sqe-menu" role="listbox" aria-label="Suggestions">
            {menu.items.map((it, i) => (
              // Mouse selection uses mousedown so the textarea keeps focus
              // (a click would blur it first and close the menu). Keyboard
              // handling lives on the textarea, per the combobox pattern.
              <div
                key={`${it.kind}:${it.label}`}
                role="option"
                aria-selected={i === active}
                tabIndex={-1}
                className={`sqe-item sqe-item-${it.kind}${i === active ? ' is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  accept(i)
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="sqe-kind" aria-hidden="true">
                  {KIND_GLYPH[it.kind]}
                </span>
                {it.label}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      {diagnostics.length ? (
        <ul className="sqe-diags" aria-label="Query problems">
          {diagnostics.map((d, i) => (
            <li key={i} className={`sqe-diag sqe-diag-${d.severity}`}>
              <button type="button" className="sqe-diag-btn" onClick={() => jumpTo(d)}>
                <span className="sqe-diag-mark" aria-hidden="true" />
                {d.message}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <span className="bui-sr-only" aria-live="polite">
        {caret >= 0 && diagnostics.length ? `${diagnostics.length} problems` : ''}
      </span>
    </div>
  )
}

const KIND_GLYPH = { stream: '▤', field: '$', keyword: 'K', function: 'ƒ' } as const

/**
 * The editor's highlight layer on its own, read-only: a query shown, not edited
 * (a workbook block in View). Same tokenizers, same `tok-*` classes, no lint.
 */
export function QueryHighlight({ value, dialect, className }: { value: string; dialect: EditorDialect; className?: string }) {
  return <pre className={className ? `qhl ${className}` : 'qhl'}>{highlight(LANG[dialect].tokenize(value), [])}</pre>
}

function highlight(tokens: Token[], diagnostics: Diagnostic[]): ReactNode[] {
  const out: ReactNode[] = []
  const worst = (start: number, end: number): Diagnostic['severity'] | null => {
    let w: Diagnostic['severity'] | null = null
    for (const d of diagnostics) {
      if (d.start < end && d.end > start) {
        if (d.severity === 'error') return 'error'
        if (d.severity === 'warn') w = 'warn'
        else if (!w) w = 'hint'
      }
    }
    return w
  }
  for (const t of tokens) {
    if (t.type === 'ws') {
      out.push(t.text)
      continue
    }
    const sev = worst(t.start, t.end)
    out.push(
      <span key={t.start} className={`tok tok-${t.type}${sev ? ` tok-${sev}` : ''}`}>
        {t.text}
      </span>,
    )
  }
  return out
}
